import React, {ReactNode} from 'react';
import {TransformValues} from '../../utils/helper';
import {CheckCircle, PixelIcon, CopyIcon, SocialShareIcon, DownloadIcon} from '../../ui-components/Iconpack';
import {SavingLoader, SavingLoaderBig, CatLoader, CatErrorBig, CatError} from '../../ui-components/loaders';
import {Button} from '../../ui-components/Button/button';
import './BulkImageUploading.css';
import {Dialog, DialogTrigger} from '../../ui-components/Modal';
import {EventNames} from '../../lib/analytics/events';
import Tracker from '../../lib/analytics/tracker';

interface ImageProps {
    imageData: TransformValues;
    isSaving: boolean;
    isSaved: boolean;
    orgUid: string;
    errorOccurred: boolean;
    onClose: () => void;
    retrySave: () => void;
}

export default function BulkImageUploading({
    imageData,
    isSaving,
    isSaved,
    errorOccurred,
    onClose,
    retrySave,
}: ImageProps) {
    const getImageQualityPercentage = (quality: string): number | undefined => {
        const qualityMap = new Map<string, number>([
            ['eco', 80],
            ['good', 85],
            ['best', 90],
            ['auto', 85],
        ]);
        return qualityMap.get(quality);
    };

    const actionsPerformed: ReactNode[] = [
        imageData.width ? <span>Image width was changed</span> : null,
        imageData.height ? <span>Image height was changed</span> : null,
        imageData.padding ? <span>Padding was added to image</span> : null,
        imageData.cropProps ? <span>Image cropped</span> : null,
        imageData.format ? <span>Image format was changed to {imageData.format}</span> : null,
        imageData.isBgRemoved ? <span>Background removal was done for the image</span> : null,
        imageData.isGreyToneApplied ? <span>Greyscale was performed on the image</span> : null,
        imageData.quality ? (
            <span>
                Image quality has been optimized to{' '}
                <span className='text-[#079455]'>{getImageQualityPercentage(imageData?.quality)}%</span>
            </span>
        ) : null,
    ].filter(Boolean) as ReactNode[];

    const onViewImage = () => {
        console.log('Add action here');
    };

    return (
        <>
            {!errorOccurred ? (
                <>
                    <div className='relative hidden md:flex'>
                        {isSaving ? <SavingLoaderBig /> : <div className='flex flex-col items-center'></div>}
                        {isSaving && (
                            <div className='absolute left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]'>
                                <PixelIcon width={90} height={78} />
                            </div>
                        )}
                    </div>
                    <div className='flex flex-col text-[1rem] font-[700] text-[#000000] md:pl-10 md:text-[30px]'>
                        <div className='relative flex items-center justify-center md:hidden'>
                            {isSaving ? <SavingLoader /> : <CatLoader />}
                            {isSaving && (
                                <div className='absolute left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]'>
                                    <PixelIcon width={48} height={41} />
                                </div>
                            )}
                        </div>
                        {isSaving && (
                            <>
                                <span>Transforming & uploading</span>
                                <span>your image to CDN</span>
                            </>
                        )}
                        {isSaved && (
                            <>
                                <span>We have transformed and uploaded</span>
                                <span>your image to CDN</span>
                            </>
                        )}

                        <div className='mt-6 flex-col overflow-y-auto md:mt-1 md:max-h-[310px] md:pt-5'>
                            {actionsPerformed.map((action: ReactNode, index: number) => (
                                <div key={index} className='flex pb-4'>
                                    <div
                                        className='line anim-typewriter-svg hidden max-w-full md:flex'
                                        style={{animationDelay: `${index * 1.5}s`}}>
                                        <CheckCircle />
                                    </div>
                                    <span
                                        className='line anim-typewriter hidden items-center gap-[11px] pl-3 text-[20px] font-[400] text-[#000000] md:flex md:text-[20px]'
                                        style={{animationDelay: `${index * 1.5}s`}}>
                                        {action}
                                    </span>
                                    <span className='flex items-center gap-[11px] pl-3 text-[12px] font-[400] text-[#000000] md:hidden md:text-[20px]'>
                                        <CheckCircle />
                                        {action}
                                    </span>
                                </div>
                            ))}
                        </div>
                        {isSaved && (
                            <>
                                <div className='hidden md:flex'>
                                    <div className='mt-20 flex gap-3'>
                                        <Dialog>
                                            <DialogTrigger>
                                                <Button
                                                    variant='secondary'
                                                    size='md'
                                                    className='h-[44px] w-[193px] text-[1rem] font-[600] text-[#344054]'
                                                    onClick={() => Tracker.trackEvent(EventNames.SHARE_IMAGE_CLICKED)}>
                                                    <SocialShareIcon width={20} height={20} />
                                                    Share Image
                                                </Button>
                                            </DialogTrigger>
                                        </Dialog>
                                        <Button
                                            variant='primary'
                                            size='md'
                                            className='h-[44px] w-[193px] text-[1rem] font-[600] text-[#FFFFFF]'
                                            icon
                                            // onClick={() => DownloadFile(imageUrl, fileName)}
                                        >
                                            <DownloadIcon />
                                            Download Image
                                        </Button>
                                    </div>
                                </div>
                                <div className='flex items-center md:hidden'>
                                    <div className='flex-col items-center justify-center'>
                                        <Button
                                            variant='secondary'
                                            size='sm'
                                            className='w-[200px]'
                                            // onClick={() => DownloadFile(imageUrl, fileName)}
                                        >
                                            Download
                                        </Button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </>
            ) : (
                <>
                    <div className='relative hidden md:flex'>
                        <CatErrorBig />
                    </div>
                    <div className='flex pl-4 text-[1rem] font-[700] text-[#000000] md:text-[30px]'>
                        <div className='flex flex-col items-center justify-center md:pl-[71px]'>
                            <div className='relative flex md:hidden'>
                                <CatError />
                            </div>
                            <span>Image upload failed</span>
                            <span className='text-[1rem] font-[400] text-[#0C111D]'>
                                We're sorry, but we encountered an error while trying to upload your image. Please
                                retry.
                            </span>
                            <div className='hidden justify-center md:flex'>
                                <div className='mt-7 flex gap-3'>
                                    <Button
                                        variant='secondary'
                                        size='md'
                                        className='h-[44px] w-[193px] text-[1rem] font-[600] text-[#344054]'
                                        onClick={onClose}>
                                        Go back to editor
                                    </Button>
                                    <Button
                                        variant='primary'
                                        size='md'
                                        className='h-[44px] w-[193px] text-[1rem] font-[600] text-[#FFFFFF]'
                                        onClick={() => {
                                            onClose();
                                            retrySave();
                                        }}>
                                        Retry
                                    </Button>
                                </div>
                            </div>
                            <div className='flex items-center md:hidden'>
                                <div className='flex-col items-center justify-center'>
                                    <Button
                                        variant='primary'
                                        size='sm'
                                        className='w-[200px]'
                                        onClick={() => {
                                            onClose();
                                            retrySave();
                                        }}>
                                        Retry
                                    </Button>
                                    <div className='pt-3'>
                                        <Button variant='secondary' size='sm' className='w-[200px]' onClick={onClose}>
                                            Go back to editor
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
